import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { api, uploadAvatar, type Me } from "../api";
import { Avatar } from "./Avatar";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationsBell } from "./NotificationsBell";

const MAX_BYTES = 512 * 1024;
const OK_TYPES = ["image/png", "image/jpeg", "image/webp"];

/**
 * The classic top-right account control: an avatar button that opens a popover with the user's
 * identity, an inline profile editor (display name + avatar upload/remove), and sign-out. Rendered
 * only for a signed-in user. `onUpdated` refetches `me` so the change propagates app-wide (e.g. the
 * rail). The operator plane is role-based — there is no "enter operator mode" here; an operator
 * account is already on the platform plane.
 */
export function AccountMenu({
  me,
  onSignOut,
  onUpdated,
  onSwitchView,
}: {
  me: Me;
  onSignOut: () => void;
  onUpdated: () => void;
  /** Operator only: flip between the platform plane and viewing one's own tenant as a client. */
  onSwitchView: (v: "client" | "operator") => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(me.name ?? "");
  const [avatar, setAvatar] = useState<string | null>(me.avatar ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function close() {
    setOpen(false);
    setEditing(false);
    setErr(null);
    setName(me.name ?? "");
  }

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function cancelEdit() {
    setEditing(false);
    setName(me.name ?? "");
    setErr(null);
  }

  // Esc closes the edit-profile modal.
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelEdit();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const displayName = me.name || me.email || "Account";
  const role = me.tenantRole ?? (me.admin ? "operator" : "member");

  async function saveName() {
    setBusy(true);
    setErr(null);
    try {
      await api("/session/profile", {
        method: "PATCH",
        body: JSON.stringify({ displayName: name.trim() || null }),
      });
      onUpdated();
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to save");
    } finally {
      setBusy(false);
    }
  }

  async function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!OK_TYPES.includes(file.type)) return setErr("Use a PNG, JPEG or WebP image.");
    if (file.size > MAX_BYTES) return setErr("Image must be 512 KB or smaller.");
    setBusy(true);
    setErr(null);
    try {
      const { avatar: url } = await uploadAvatar(file);
      // Same URL + no-store on the server; bust the cache so the new image shows at once.
      setAvatar(url ? `${url}?t=${Date.now()}` : null);
      onUpdated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeAvatar() {
    setBusy(true);
    setErr(null);
    try {
      const { avatar: url } = await api<{ avatar: string | null }>("/session/avatar", { method: "DELETE" });
      setAvatar(url ? `${url}?t=${Date.now()}` : null);
      onUpdated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to remove");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <div className="account-menu" ref={ref}>
      {/* Header cluster: appearance toggle (sun/moon) + notification bell + the avatar/account popover. */}
      <ThemeToggle iconOnly />
      <NotificationsBell />
      <button className="account-btn" aria-label="Account" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Avatar name={me.name} email={me.email} src={avatar} size={34} />
      </button>

      {open && (
        <div className="account-pop">
          <div className="account-head">
            <Avatar name={me.name} email={me.email} src={avatar} size={44} />
            <div className="account-id">
              <div className="account-name">{displayName}</div>
              {me.email && <div className="account-email">{me.email}</div>}
              <div className="account-role">{role}</div>
            </div>
          </div>

          <div className="account-actions">
            <button
              className="account-item"
              onClick={() => {
                setName(me.name ?? "");
                setErr(null);
                setEditing(true);
                setOpen(false); // hand off from the popover to the modal
              }}
            >
              Edit profile
            </button>
            {/* Operator only: toggle between the platform plane and your own client workspace. */}
            {me.operator && (
              <button
                className="account-item"
                onClick={() => {
                  onSwitchView(me.admin ? "client" : "operator");
                  close();
                }}
              >
                {me.admin ? "View my workspace" : "Switch to operator"}
              </button>
            )}
            <button className="account-item" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>

      {editing && (
        <div className="modal-backdrop account-modal" onClick={cancelEdit}>
          <div className="modal account-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="section-head">
              <h2>Edit profile</h2>
              <button className="btn ghost sm" disabled={busy} onClick={cancelEdit}>
                Close
              </button>
            </div>
            <div className="account-modal-body">
              <div className="account-modal-avatar">
                <Avatar name={me.name} email={me.email} src={avatar} size={64} />
                <div className="account-avatar-actions">
                  <button className="quiet-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
                    Upload photo
                  </button>
                  {avatar && (
                    <button className="quiet-btn" disabled={busy} onClick={() => void removeAvatar()}>
                      Remove
                    </button>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={onPickFile}
                  />
                </div>
              </div>
              <label className="account-field">
                <span>Display name</span>
                <input
                  value={name}
                  disabled={busy}
                  maxLength={80}
                  autoFocus
                  placeholder={me.email ?? "Your name"}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              {err && <div className="err sm">{err}</div>}
            </div>
            <div className="account-edit-actions">
              <button className="btn" disabled={busy} onClick={() => void saveName()}>
                {busy ? "Saving…" : "Save changes"}
              </button>
              <button className="quiet-btn" disabled={busy} onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
